import React, { useState, useEffect } from 'react';
import { Plus, X, ChevronLeft, ChevronRight, CheckCircle, Trash2, Video, ExternalLink, BarChart3 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { updateBookingStatus, deleteBooking, createCustomSlot, getCalendarData, getCategoryStats } from '../../utils/api';

const getStatusBadge = (status) => {
  const styles = {
    pending: 'bg-yellow-100 text-yellow-800', payment_uploaded: 'bg-orange-100 text-orange-800',
    approved: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800',
    'ulas-pending': 'bg-yellow-100 text-yellow-800', 'ulas-rejected': 'bg-red-100 text-red-800',
    completed: 'bg-blue-100 text-blue-800', cancelled: 'bg-gray-100 text-gray-800',
    'auto-cancelled': 'bg-gray-100 text-gray-800', 'reschedule-requested': 'bg-purple-100 text-purple-800',
    'refund-requested': 'bg-pink-100 text-pink-800'
  };
  return styles[status] || styles.pending;
};

export const AdminBookings = ({ bookings, calendarData, stats, currentMonth: initMonth, currentYear: initYear, loadData, onCalendarChange }) => {
  const [bookingFilter, setBookingFilter] = useState('all');
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showAddSlotModal, setShowAddSlotModal] = useState(false);
  const [newSlotDate, setNewSlotDate] = useState('');
  const [newSlotTime, setNewSlotTime] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [viberLink, setViberLink] = useState('');
  const [currentMonth, setCurrentMonth] = useState(initMonth);
  const [currentYear, setCurrentYear] = useState(initYear);
  
  // Reschedule approval state
  const [showRescheduleApprovalModal, setShowRescheduleApprovalModal] = useState(false);
  const [rescheduleApprovalDate, setRescheduleApprovalDate] = useState('');
  const [rescheduleApprovalSlot, setRescheduleApprovalSlot] = useState('');
  const [rescheduleRejectionNote, setRescheduleRejectionNote] = useState('');
  const [showRescheduleRejectModal, setShowRescheduleRejectModal] = useState(false);
  
  // Category stats
  const [categoryStats, setCategoryStats] = useState({});
  const [showCategoryStats, setShowCategoryStats] = useState(false);

  useEffect(() => {
    getCategoryStats().then(res => setCategoryStats(res.data)).catch(() => {});
  }, []);

  const filteredBookings = bookingFilter === 'all' ? bookings : bookings.filter(b => b.status === bookingFilter);

  const navigateMonth = (direction) => {
    let newMonth = currentMonth + direction;
    let newYear = currentYear;
    if (newMonth > 12) { newMonth = 1; newYear++; } else if (newMonth < 1) { newMonth = 12; newYear--; }
    setCurrentMonth(newMonth); setCurrentYear(newYear);
    if (onCalendarChange) onCalendarChange(newMonth, newYear);
  };

  const handleUpdateBookingStatus = async (bookingId, status, additionalData = {}) => {
    try {
      const payload = { ...additionalData };
      if (status) payload.status = status;
      await updateBookingStatus(bookingId, payload);
      const msgs = { 'approved': 'Booking approved!', 'rejected': 'Booking rejected.', 'completed': 'Booking completed.', 'reschedule-requested': 'Reschedule request sent.' };
      const msg = status ? (msgs[status] || `Booking ${status}`) : (additionalData.ulas_status === 'verified' ? 'ULAS verified & approved!' : additionalData.ulas_status === 'rejected' ? 'ULAS rejected. Client notified.' : 'Booking updated.');
      toast.success(msg);
      setShowBookingModal(false); setSelectedBooking(null); setShowRejectModal(false); setShowRescheduleModal(false);
      setRejectionReason(''); setRescheduleReason('');
      loadData();
    } catch (error) { toast.error('Failed to update booking'); }
  };

  const handleAddCustomSlot = async () => {
    if (!newSlotDate || !newSlotTime) { toast.error('Please select date and time'); return; }
    try {
      await createCustomSlot({ date: newSlotDate, time_slot: newSlotTime, is_available: true });
      toast.success('Custom slot added');
      setShowAddSlotModal(false); setNewSlotDate(''); setNewSlotTime('');
      if (onCalendarChange) onCalendarChange(currentMonth, currentYear);
    } catch (error) { toast.error('Failed to add slot'); }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200"><p className="text-sm text-slate-600">Total</p><p className="text-2xl font-bold text-slate-900">{stats.totalBookings}</p></div>
        <div className="bg-yellow-50 rounded-lg p-4 shadow-sm border border-yellow-200"><p className="text-sm text-yellow-700">Pending</p><p className="text-2xl font-bold text-yellow-800">{bookings.filter(b => b.status === 'pending' || b.status === 'payment_uploaded').length}</p></div>
        <div className="bg-green-50 rounded-lg p-4 shadow-sm border border-green-200"><p className="text-sm text-green-700">Approved</p><p className="text-2xl font-bold text-green-800">{bookings.filter(b => b.status === 'approved').length}</p></div>
        <div className="bg-blue-50 rounded-lg p-4 shadow-sm border border-blue-200"><p className="text-sm text-blue-700">Completed</p><p className="text-2xl font-bold text-blue-800">{bookings.filter(b => b.status === 'completed').length}</p></div>
        <div className="bg-red-50 rounded-lg p-4 shadow-sm border border-red-200"><p className="text-sm text-red-700">Cancelled</p><p className="text-2xl font-bold text-red-800">{bookings.filter(b => b.status === 'cancelled' || b.status === 'auto-cancelled').length}</p></div>
        <div className="bg-slate-50 rounded-lg p-4 shadow-sm border border-slate-300"><p className="text-sm text-slate-600">Rejected</p><p className="text-2xl font-bold text-slate-800">{bookings.filter(b => b.status === 'rejected').length}</p></div>
      </div>

      {/* Legal Concern Category Stats */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200">
        <button onClick={() => setShowCategoryStats(!showCategoryStats)} className="flex items-center justify-between w-full">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#8B1818]" /> Legal Concern Categories</h3>
          <span className="text-xs text-slate-500">{showCategoryStats ? 'Hide' : 'Show'}</span>
        </button>
        {showCategoryStats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-4">
            {[
              { key: 'Government Procurement', color: 'bg-blue-50 border-blue-200 text-blue-800' },
              { key: 'Notarization', color: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
              { key: 'Corporate Questions', color: 'bg-violet-50 border-violet-200 text-violet-800' },
              { key: 'Public Officer Accountability', color: 'bg-amber-50 border-amber-200 text-amber-800' },
              { key: 'Other Legal-related Matters', color: 'bg-slate-50 border-slate-300 text-slate-800' },
              { key: 'App Development', color: 'bg-cyan-50 border-cyan-200 text-cyan-800' }
            ].map(cat => (
              <div key={cat.key} className={`rounded-lg p-3 border ${cat.color} text-center`} data-testid={`category-stat-${cat.key.replace(/\s+/g, '-').toLowerCase()}`}>
                <p className="text-2xl font-bold">{categoryStats[cat.key] || 0}</p>
                <p className="text-xs font-medium mt-1 leading-tight">{cat.key}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-slate-900">Consultation Bookings</h2>
            <select value={bookingFilter} onChange={(e) => setBookingFilter(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm">
              {['all','pending','payment_uploaded','approved','ulas-pending','ulas-rejected','completed','cancelled','auto-cancelled','rejected','reschedule-requested','refund-requested'].map(f => <option key={f} value={f}>{f === 'all' ? 'All' : f.replace(/-/g,' ').replace(/_/g,' ')}</option>)}
            </select>
          </div>
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {filteredBookings.length === 0 ? <p className="text-slate-500 text-center py-8">No bookings found</p> : filteredBookings.map((booking) => (
              <div key={booking.id} onClick={() => { setSelectedBooking(booking); setShowBookingModal(true); }} className="p-4 border border-slate-200 rounded-lg hover:border-[#8B1818] cursor-pointer transition-colors">
                <div className="flex justify-between items-start">
                  <div><p className="font-semibold text-slate-900">{booking.user_name}</p><p className="text-sm text-slate-600">{booking.date} at {booking.time_slot}</p><p className="text-sm text-slate-600">{booking.duration}hr - &#8369;{booking.total_amount?.toLocaleString()}</p>{booking.payment_proof_url && <p className="text-xs text-green-600 mt-1">Payment proof uploaded</p>}</div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(booking.status)}`}>{booking.status === 'auto-cancelled' ? 'Auto-Cancelled' : booking.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-slate-900">Calendar</h2>
            <div className="flex items-center gap-2">
              <button onClick={() => navigateMonth(-1)} className="p-2 hover:bg-slate-100 rounded"><ChevronLeft className="h-5 w-5" /></button>
              <span className="font-medium min-w-[120px] text-center">{new Date(currentYear, currentMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
              <button onClick={() => navigateMonth(1)} className="p-2 hover:bg-slate-100 rounded"><ChevronRight className="h-5 w-5" /></button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-4">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">{day}</div>)}
            {(() => {
              const firstDay = new Date(currentYear, currentMonth - 1, 1).getDay();
              const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
              const days = [];
              for (let i = 0; i < firstDay; i++) days.push(<div key={`empty-${i}`} className="h-10"></div>);
              for (let day = 1; day <= daysInMonth; day++) {
                const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const dayBookings = calendarData?.bookings_by_date?.[dateStr] || [];
                const hasBookings = dayBookings.length > 0;
                const hasPending = dayBookings.some(b => b.status === 'pending');
                const dayOfWeek = new Date(currentYear, currentMonth - 1, day).getDay();
                const isDefaultDay = dayOfWeek === 2 || dayOfWeek === 4;
                days.push(<div key={day} className={`h-10 flex flex-col items-center justify-center rounded text-sm relative ${isDefaultDay ? 'bg-green-50' : ''} ${hasBookings ? 'font-bold' : ''}`}>{day}{hasBookings && <div className={`absolute bottom-1 w-2 h-2 rounded-full ${hasPending ? 'bg-yellow-500' : 'bg-green-500'}`}></div>}</div>);
              }
              return days;
            })()}
          </div>
          <div className="flex gap-4 text-xs text-slate-600 mt-4">
            <span className="flex items-center gap-1"><div className="w-3 h-3 bg-green-50 rounded"></div> Tue/Thu (Default)</span>
            <span className="flex items-center gap-1"><div className="w-2 h-2 bg-yellow-500 rounded-full"></div> Pending</span>
            <span className="flex items-center gap-1"><div className="w-2 h-2 bg-green-500 rounded-full"></div> Confirmed</span>
          </div>
          <Button onClick={() => setShowAddSlotModal(true)} className="w-full mt-4 bg-[#8B1818] text-white hover:bg-[#701313]"><Plus className="h-4 w-4 mr-2" />Add Custom Time Slot</Button>
        </div>
      </div>

      {showBookingModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-900">Booking Details</h3>
              <button onClick={() => { setShowBookingModal(false); setSelectedBooking(null); }}><X className="h-6 w-6 text-slate-500" /></button>
            </div>
            <div className="space-y-4">
              {[['Status', <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(selectedBooking.status)}`}>{selectedBooking.status}</span>],
                ['Client', selectedBooking.user_name], ['Email', selectedBooking.user_email], ['Viber', selectedBooking.viber_contact],
                ['Date & Time', `${selectedBooking.date} at ${selectedBooking.time_slot}`], ['Duration', `${selectedBooking.duration} hour(s)`],
                ['Amount', <span className="font-bold text-[#8B1818]">&#8369;{selectedBooking.total_amount?.toLocaleString()}</span>],
                ['Payment Method', selectedBooking.payment_method?.toUpperCase()],
                ...(selectedBooking.legal_category ? [['Legal Concern', selectedBooking.legal_category]] : []),
                ...(selectedBooking.is_ulas ? [['ULAS', 'Free Legal Aid']] : [])
              ].map(([label, value], i) => <div key={i} className="flex justify-between"><span className="text-slate-600">{label}</span><span className="font-medium">{value}</span></div>)}
              {selectedBooking.payment_proof_url && <div><span className="text-slate-600 block mb-2">Payment Proof</span><a href={selectedBooking.payment_proof_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm break-all">View Payment Proof</a></div>}
              {selectedBooking.notes && <div><span className="text-slate-600 block mb-1">Client Notes</span><p className="text-sm bg-slate-50 p-3 rounded">{selectedBooking.notes}</p></div>}
              {selectedBooking.invoice_number && <div className="flex justify-between"><span className="text-slate-600">Invoice #</span><span className="font-medium">{selectedBooking.invoice_number}</span></div>}
            </div>
            {selectedBooking.status === 'pending' && <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200"><p className="text-sm text-yellow-800">Waiting for payment proof. Auto-cancels in 2 hours.</p></div>}
            {selectedBooking.status === 'payment_uploaded' && (
              <div className="space-y-3 mt-6">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200"><p className="text-sm text-green-800">Payment proof uploaded. Please verify.</p></div>
                <div className="flex gap-3">
                  <Button onClick={() => handleUpdateBookingStatus(selectedBooking.id, 'approved')} className="flex-1 bg-green-600 text-white hover:bg-green-700"><CheckCircle className="h-4 w-4 mr-2" />Confirm</Button>
                  <Button onClick={() => setShowRejectModal(true)} variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50">Reject</Button>
                </div>
                <Button onClick={() => setShowRescheduleModal(true)} variant="outline" className="w-full border-purple-300 text-purple-600 hover:bg-purple-50">Request Reschedule</Button>
              </div>)}
            {/* ULAS Pending Verification */}
            {selectedBooking.status === 'ulas-pending' && (
              <div className="space-y-3 mt-6">
                <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <p className="text-sm text-indigo-800 font-semibold mb-2">ULAS Free Legal Aid — Pending Verification</p>
                  <p className="text-xs text-indigo-700 mb-2">This client applied under the Unified Legal Aid Service (ULAS) rules. Amount: ₱0</p>
                  {selectedBooking.ulas_declaration_url ? (
                    <a href={selectedBooking.ulas_declaration_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm" data-testid="view-ulas-declaration">
                      View Qualified Beneficiary Declaration
                    </a>
                  ) : (
                    <p className="text-xs text-amber-700">Declaration not yet uploaded by client.</p>
                  )}
                </div>
                {selectedBooking.ulas_declaration_url && (
                  <div className="space-y-2">
                    <textarea
                      value={rescheduleReason}
                      onChange={(e) => setRescheduleReason(e.target.value)}
                      placeholder="Admin notes on ULAS verification (optional)..."
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      rows={2}
                      data-testid="ulas-admin-notes-input"
                    />
                    <div className="flex gap-3">
                      <Button
                        onClick={() => handleUpdateBookingStatus(selectedBooking.id, null, { ulas_status: 'verified', ulas_admin_notes: rescheduleReason || 'Verified' })}
                        className="flex-1 bg-green-600 text-white hover:bg-green-700"
                        data-testid="verify-ulas-btn"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />Verify & Approve
                      </Button>
                      <Button
                        onClick={() => handleUpdateBookingStatus(selectedBooking.id, null, { ulas_status: 'rejected', ulas_admin_notes: rescheduleReason || 'Not a qualified beneficiary' })}
                        variant="outline"
                        className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                        data-testid="reject-ulas-btn"
                      >
                        Reject ULAS
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* ULAS Rejected */}
            {selectedBooking.status === 'ulas-rejected' && (
              <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm text-red-800 font-semibold mb-1">ULAS Beneficiary Not Qualified</p>
                {selectedBooking.ulas_admin_notes && <p className="text-sm text-red-700">{selectedBooking.ulas_admin_notes}</p>}
                <p className="text-xs text-red-600 mt-1">The client has been notified and may rebook as a paid consultation.</p>
              </div>
            )}
            {selectedBooking.status === 'approved' && (
              <div className="space-y-3 mt-6">
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <Label className="text-sm font-semibold text-purple-900 mb-2 block flex items-center"><Video className="h-4 w-4 mr-2" />Viber Meeting Link</Label>
                  <div className="flex gap-2">
                    <Input value={viberLink || selectedBooking.viber_link || ''} onChange={(e) => setViberLink(e.target.value)} placeholder="Paste Viber group/meeting link here..." className="flex-1" data-testid="viber-link-input" />
                    <Button size="sm" className="bg-purple-600 text-white hover:bg-purple-700" onClick={async () => {
                      const link = viberLink || selectedBooking.viber_link;
                      if (!link?.trim()) { toast.error('Please enter a Viber link'); return; }
                      try { await updateBookingStatus(selectedBooking.id, { viber_link: link.trim() }); toast.success('Viber link saved and shared with client!'); loadData(); } catch (e) { toast.error('Failed to save link'); }
                    }} data-testid="save-viber-link-btn">Save Link</Button>
                  </div>
                  {(selectedBooking.viber_link) && <p className="text-xs text-purple-700 mt-2 flex items-center"><CheckCircle className="h-3 w-3 mr-1" />Link shared with client — they can access it from their bookings page.</p>}
                </div>
                <Button onClick={() => handleUpdateBookingStatus(selectedBooking.id, 'completed')} className="w-full bg-blue-600 text-white hover:bg-blue-700">Mark as Completed</Button>
                <Button onClick={() => setShowRescheduleModal(true)} variant="outline" className="w-full border-purple-300 text-purple-600 hover:bg-purple-50">Request Reschedule</Button>
              </div>)}
            {selectedBooking.status === 'rejected' && selectedBooking.rejection_reason && <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200"><p className="text-sm text-red-800"><strong>Rejection Reason:</strong> {selectedBooking.rejection_reason}</p></div>}
            {selectedBooking.status === 'reschedule-requested' && (
              <div className="mt-6 space-y-3">
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-800 font-semibold mb-2">Reschedule Request</p>
                  {selectedBooking.reschedule_requested_by === 'user' && (
                    <p className="text-xs text-purple-700 mb-1">Requested by: Client</p>
                  )}
                  {selectedBooking.reschedule_reason && (
                    <p className="text-sm text-purple-700"><strong>Reason:</strong> {selectedBooking.reschedule_reason}</p>
                  )}
                  {selectedBooking.requested_new_date && (
                    <p className="text-sm text-purple-700 mt-1">
                      <strong>Preferred:</strong> {selectedBooking.requested_new_date} at {selectedBooking.requested_new_time_slot}
                    </p>
                  )}
                  {selectedBooking.reschedule_document_url && (
                    <a href={selectedBooking.reschedule_document_url} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-sm text-blue-600 hover:underline" data-testid="view-reschedule-document">
                      View Supporting Document
                    </a>
                  )}
                </div>
                {selectedBooking.reschedule_requested_by === 'user' && (
                  <div className="flex gap-3">
                    <Button
                      onClick={() => {
                        setRescheduleApprovalDate(selectedBooking.requested_new_date || '');
                        setRescheduleApprovalSlot(selectedBooking.requested_new_time_slot || '');
                        setShowRescheduleApprovalModal(true);
                      }}
                      className="flex-1 bg-green-600 text-white hover:bg-green-700"
                      data-testid="approve-reschedule-btn"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />Approve Reschedule
                    </Button>
                    <Button
                      onClick={() => {
                        setRescheduleRejectionNote('');
                        setShowRescheduleRejectModal(true);
                      }}
                      variant="outline"
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      data-testid="reject-reschedule-btn"
                    >
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            )}
            {selectedBooking.status === 'refund-requested' && <div className="mt-6 p-4 bg-pink-50 rounded-lg border border-pink-200"><p className="text-sm text-pink-800">Subscriber requested refund. Process within 3 business days.</p></div>}
            {(selectedBooking.status === 'cancelled' || selectedBooking.status === 'auto-cancelled') && <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200"><p className="text-sm text-gray-800">{selectedBooking.status === 'auto-cancelled' ? 'Auto-cancelled due to no payment proof.' : 'Cancelled by subscriber.'}</p></div>}
            <Button onClick={async () => { if (!window.confirm('Delete this booking?')) return; try { await deleteBooking(selectedBooking.id); toast.success('Booking deleted'); setShowBookingModal(false); setSelectedBooking(null); loadData(); } catch(e) { toast.error('Failed'); } }} variant="ghost" className="w-full mt-2 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4 mr-2" />Delete Booking</Button>
          </div>
        </div>)}

      {showRejectModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Reject Booking</h3>
            <p className="text-sm text-slate-600 mb-4">Subscriber will be notified and receive a refund within 3 business days.</p>
            <div className="mb-4"><Label>Reason (optional)</Label><textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-lg text-sm" rows={3} placeholder="Enter reason..." /></div>
            <div className="flex gap-3"><Button onClick={() => handleUpdateBookingStatus(selectedBooking.id, 'rejected', { rejection_reason: rejectionReason })} className="flex-1 bg-red-600 text-white hover:bg-red-700">Confirm Rejection</Button><Button onClick={() => { setShowRejectModal(false); setRejectionReason(''); }} variant="outline" className="flex-1">Cancel</Button></div>
          </div>
        </div>)}

      {showRescheduleModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Request Reschedule</h3>
            <p className="text-sm text-slate-600 mb-4">Subscriber can select a new time or request a refund.</p>
            <div className="mb-4"><Label>Reason (optional)</Label><textarea value={rescheduleReason} onChange={(e) => setRescheduleReason(e.target.value)} className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-lg text-sm" rows={3} placeholder="Enter reason..." /></div>
            <div className="flex gap-3"><Button onClick={() => handleUpdateBookingStatus(selectedBooking.id, 'reschedule-requested', { reschedule_reason: rescheduleReason })} className="flex-1 bg-purple-600 text-white hover:bg-purple-700">Send Request</Button><Button onClick={() => { setShowRescheduleModal(false); setRescheduleReason(''); }} variant="outline" className="flex-1">Cancel</Button></div>
          </div>
        </div>)}

      {showAddSlotModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4"><h3 className="text-xl font-bold text-slate-900">Add Custom Time Slot</h3><button onClick={() => setShowAddSlotModal(false)}><X className="h-6 w-6 text-slate-500" /></button></div>
            <p className="text-slate-600 mb-4 text-sm">Add a time slot outside the regular schedule.</p>
            <div className="space-y-4">
              <div><Label>Date</Label><Input type="date" value={newSlotDate} onChange={(e) => setNewSlotDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="mt-2" /></div>
              <div><Label>Time Slot</Label><select value={newSlotTime} onChange={(e) => setNewSlotTime(e.target.value)} className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-lg"><option value="">Select time</option>{['09:00','10:00','11:00','13:00','14:00','15:00','16:00'].map(t => <option key={t} value={t}>{parseInt(t) > 12 ? `${parseInt(t) - 12}:00 PM` : `${parseInt(t)}:00 AM`}</option>)}</select></div>
            </div>
            <div className="flex gap-3 mt-6"><Button onClick={handleAddCustomSlot} className="flex-1 bg-[#8B1818] text-white hover:bg-[#701313]">Add Slot</Button><Button variant="outline" onClick={() => setShowAddSlotModal(false)}>Cancel</Button></div>
          </div>
        </div>)}

      {showRescheduleApprovalModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" data-testid="reschedule-approval-modal">
            <h3 className="text-xl font-bold text-slate-900 mb-2">Approve Reschedule</h3>
            <p className="text-sm text-slate-600 mb-4">
              Accept the client's suggested date/time or choose a different one.
            </p>
            {selectedBooking.requested_new_date && (
              <div className="bg-purple-50 p-3 rounded-lg mb-4 text-sm">
                <p className="text-purple-800"><strong>Client's preference:</strong></p>
                <p className="text-purple-700">{selectedBooking.requested_new_date} at {selectedBooking.requested_new_time_slot}</p>
              </div>
            )}
            <div className="space-y-4">
              <div>
                <Label>New Date</Label>
                <Input
                  type="date"
                  value={rescheduleApprovalDate}
                  onChange={(e) => setRescheduleApprovalDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="mt-2"
                  data-testid="reschedule-approval-date"
                />
              </div>
              <div>
                <Label>New Time Slot</Label>
                <select
                  value={rescheduleApprovalSlot}
                  onChange={(e) => setRescheduleApprovalSlot(e.target.value)}
                  className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-lg"
                  data-testid="reschedule-approval-slot"
                >
                  <option value="">Select time</option>
                  {['09:00 AM','10:00 AM','11:00 AM','01:00 PM','02:00 PM','03:00 PM','04:00 PM'].map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => {
                  if (!rescheduleApprovalDate || !rescheduleApprovalSlot) { toast.error('Please select date and time'); return; }
                  handleUpdateBookingStatus(selectedBooking.id, 'approved', { new_date: rescheduleApprovalDate, new_time_slot: rescheduleApprovalSlot });
                  setShowRescheduleApprovalModal(false);
                }}
                className="flex-1 bg-green-600 text-white hover:bg-green-700"
                data-testid="confirm-reschedule-approval-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />Confirm
              </Button>
              <Button onClick={() => setShowRescheduleApprovalModal(false)} variant="outline" className="flex-1">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {showRescheduleRejectModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" data-testid="reschedule-reject-modal">
            <h3 className="text-xl font-bold text-slate-900 mb-2">Reject Reschedule Request</h3>
            <p className="text-sm text-slate-600 mb-4">
              The client's original booking date will be retained. They will be notified of the rejection reason.
            </p>
            <div className="mb-4">
              <Label>Reason (will be shown to client)</Label>
              <textarea
                value={rescheduleRejectionNote}
                onChange={(e) => setRescheduleRejectionNote(e.target.value)}
                className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                rows={3}
                placeholder="Explain why the reschedule is not possible..."
                data-testid="reschedule-reject-reason-input"
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  handleUpdateBookingStatus(selectedBooking.id, 'reschedule-rejected', {
                    reschedule_rejection_note: rescheduleRejectionNote || 'Reschedule request was not approved.'
                  });
                  setShowRescheduleRejectModal(false);
                }}
                className="flex-1 bg-red-600 text-white hover:bg-red-700"
                data-testid="confirm-reschedule-reject-btn"
              >
                Confirm Rejection
              </Button>
              <Button onClick={() => setShowRescheduleRejectModal(false)} variant="outline" className="flex-1">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
